import React, { useState } from 'react';
import { Box, Grid, Typography, Card, CardContent, Avatar, Rating, IconButton, useMediaQuery, useTheme, alpha } from '@mui/material';
import SectionContainer from './SectionContainer';
import AnimatedElement from './AnimatedElement';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';

const testimonials = [
  {
    id: 1,
    name: 'Александр Петров',
    role: 'Инфобизнесмен',
    avatar: 'https://randomuser.me/api/portraits/men/1.jpg',
    rating: 5,
    text: 'После внедрения этой системы доходимость на мои вебинары увеличилась на 22%. Люди легко подключаются прямо из Telegram и больше не пропускают важные вебинары. Очень удобно управлять презентацией и призывами к действию. Рекомендую!',
  },
  {
    id: 2,
    name: 'Екатерина Смирнова',
    role: 'Маркетолог, автор онлайн-курсов',
    avatar: 'https://randomuser.me/api/portraits/women/2.jpg',
    rating: 5,
    text: 'Отличный сервис, который решил мою главную проблему – сбор реальных контактов участников. Теперь я могу эффективно работать с аудиторией после вебинара, что заметно повысило конверсию в продажи курса. И всё это без лишних затрат на дорогие платформы.',
  },
  {
    id: 3,
    name: 'Максим Иванов',
    role: 'Основатель онлайн-школы',
    avatar: 'https://randomuser.me/api/portraits/men/3.jpg',
    rating: 4,
    text: 'Провожу вебинары еженедельно и раньше использовал дорогие платформы. С переходом на этот сервис экономлю порядка 15000₽ в месяц. Функционал интуитивно понятен, а техподдержка всегда оперативно отвечает на вопросы.',
  },
  {
    id: 4,
    name: 'Ольга Николаева',
    role: 'Бизнес-тренер',
    avatar: 'https://randomuser.me/api/portraits/women/4.jpg',
    rating: 5,
    text: 'Проводила вебинар впервые и очень переживала, но всё прошло гладко! Участникам не нужно было регистрироваться на сторонних сайтах, все просто перешли по ссылке в Telegram. Конверсия в продажи составила 24%, что превзошло мои ожидания.',
  },
  {
    id: 5,
    name: 'Дмитрий Козлов',
    role: 'Коуч по личностному росту',
    avatar: 'https://randomuser.me/api/portraits/men/5.jpg',
    rating: 5,
    text: 'Раньше на мои вебинары записывалось много людей, но реально приходило меньше половины. После перехода на этот сервис доходимость увеличилась до 85%. Удобная интеграция с YouTube, красивый интерфейс и функциональные возможности. Однозначно рекомендую!',
  },
];

const TestimonialCard = ({ testimonial }) => {
  const theme = useTheme();
  
  return (
    <Card
      elevation={0}
      sx={{
        height: '100%',
        borderRadius: 4,
        position: 'relative',
        border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
        transition: 'all 0.3s ease',
        '&:hover': {
          boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
          transform: 'translateY(-8px)',
        },
      }}
    >
      <CardContent sx={{ p: 4 }}>
        <FormatQuoteIcon
          sx={{
            position: 'absolute',
            top: 16,
            right: 16,
            color: alpha(theme.palette.primary.main, 0.1),
            fontSize: 40,
          }}
        />
        
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Avatar
            src={testimonial.avatar}
            alt={testimonial.name}
            sx={{ width: 64, height: 64, mr: 2 }}
          />
          <Box>
            <Typography variant="h6" fontWeight="bold">
              {testimonial.name}
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {testimonial.role}
            </Typography>
            <Rating value={testimonial.rating} readOnly size="small" />
          </Box>
        </Box>
        
        <Typography variant="body1" color="text.secondary">
          {testimonial.text}
        </Typography>
      </CardContent>
    </Card>
  );
};

const TestimonialsSection = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  
  const [currentIndex, setCurrentIndex] = useState(0);
  
  const cardsToShow = isMobile ? 1 : isTablet ? 2 : 3;
  const maxIndex = testimonials.length - cardsToShow;
  
  const handlePrev = () => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : 0));
  };
  
  const handleNext = () => {
    setCurrentIndex((prev) => (prev < maxIndex ? prev + 1 : maxIndex));
  };
  
  const visibleTestimonials = testimonials.slice(currentIndex, currentIndex + cardsToShow);
  
  return (
    <SectionContainer
      id="testimonials"
      title="Отзывы наших клиентов"
      subtitle="Узнайте, что говорят организаторы вебинаров, которые уже используют наш сервис"
    >
      <Box sx={{ position: 'relative', px: { xs: 0, md: 8 } }}>
        <Grid container spacing={3}>
          {visibleTestimonials.map((testimonial, index) => (
            <Grid item xs={12} md={6} lg={4} key={testimonial.id}>
              <AnimatedElement animation="fadeIn" delay={0.2 * index}>
                <TestimonialCard testimonial={testimonial} />
              </AnimatedElement>
            </Grid>
          ))}
        </Grid>
        
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            mt: 4,
            gap: 2,
          }}
        >
          <IconButton
            onClick={handlePrev}
            disabled={currentIndex === 0}
            sx={{
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              color: currentIndex === 0 ? 'text.disabled' : 'primary.main',
              '&:hover': {
                bgcolor: alpha(theme.palette.primary.main, 0.2),
              },
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          
          <IconButton
            onClick={handleNext}
            disabled={currentIndex >= maxIndex}
            sx={{
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              color: currentIndex >= maxIndex ? 'text.disabled' : 'primary.main',
              '&:hover': {
                bgcolor: alpha(theme.palette.primary.main, 0.2),
              },
            }}
          >
            <ArrowForwardIcon />
          </IconButton>
        </Box>
      </Box>
    </SectionContainer>
  );
};

export default TestimonialsSection; 